"use client";
import { useLazyVisible } from "@/hooks/useLazyVisible";
import React, { PropsWithChildren, ReactNode, useEffect, useRef } from "react";

interface Props {
  /** Estimated section height (px) reserved before mount — prevents layout shift. */
  minHeight?: number;
  /**
   * Height floor kept AFTER mount. Only pass a MEASURED height here — leave it
   * undefined for section types whose height was never measured. `minHeight`'s
   * generic fallback is a guess that is fine as a pre-mount reservation but
   * wrong as a permanent floor: a ~35px `html_blocks` promo strip pinned to
   * 240px leaves a visible blank band under it for the rest of the page's life.
   */
  mountedMinHeight?: number;
  /** Render immediately (for an above-the-fold section). */
  eager?: boolean;
  /** Section-typed skeleton shown until the real section mounts. */
  fallback?: ReactNode;
  /** Unique key for this section — used to persist visibility across navigations. */
  sectionKey?: string;
}

/**
 * Module-level set of section keys that have already been revealed.
 * Survives soft navigations (back/forward) within the same SPA session so
 * returning to the home page skips skeletons for sections the user already saw.
 */
const revealedSections = new Set<string>();

const ElementWapper: React.FC<PropsWithChildren<Props>> = ({
  children,
  minHeight = 377,
  mountedMinHeight,
  eager = false,
  fallback,
  sectionKey,
}) => {
  // If this section was previously revealed in this session, skip the skeleton
  const wasRevealed = sectionKey ? revealedSections.has(sectionKey) : false;
  const effectiveEager = eager || wasRevealed;

  // Stable rootMargin → the IntersectionObserver is created once and never recreated.
  // Sections mount ~1 screen ahead, progressively, as the user scrolls — instead of the
  // old `useInteractionStart` flag flipping every wrapper at once on the first scroll and
  // mass-mounting 2-3 screens of sections in a single frame (the scroll-freeze).
  const { ref, isVisible } = useLazyVisible("600px", true, effectiveEager);

  // Record that this section has been revealed so back-navigation skips the skeleton
  const recorded = useRef(false);
  useEffect(() => {
    if (isVisible && sectionKey && !recorded.current) {
      recorded.current = true;
      revealedSections.add(sectionKey);
    }
  }, [isVisible, sectionKey]);

  return (
    <div
      ref={ref}
      // While still below the fold, `content-visibility` lets the browser skip painting
      // (and animating) the placeholder, so off-screen skeleton shimmers cost nothing;
      // `contain-intrinsic-size` reserves the height so the scrollbar stays stable.
      style={
        !isVisible
          ? {
              contentVisibility: "auto",
              // A plain length, not `auto ${minHeight}px`: the `auto` keyword makes the
              // reserved height come from the element's LAST REMEMBERED size, so a
              // placeholder that was ever laid out short keeps that height instead of the
              // reservation. Only applies while unmounted — the mounted branch below
              // carries no containment at all.
              containIntrinsicSize: `${minHeight}px`,
            }
          : // Keep reserving the height AFTER mounting too. Most builder sections
            // are `dynamic()` with no `loading` fallback, so for the moment between
            // "visible" and "chunk downloaded" the child renders nothing — and
            // because this branch used to drop all sizing hints, the section
            // collapsed to 0px and then snapped back once the chunk arrived. That
            // collapse was the largest layout shift left on the home page.
            // `mountedMinHeight` is a floor, so taller content is unaffected — and
            // it is undefined for unmeasured types, which leaves no floor at all
            // rather than pinning a short section to a guessed height.
            { minHeight: mountedMinHeight }
      }
    >
      {isVisible ? children : (fallback ?? <div style={{ width: "100%", minHeight }} />)}
    </div>
  );
};

export default ElementWapper;
